// ═══════════════════════════════════════════
// CONFIGURACIÓN BASE
// ═══════════════════════════════════════════

export interface ApiConfig {
  baseURL: string;
  timeout?: number;
  headers?: Record<string, string>;
  auth?: AuthConfig;
  retry?: RetryConfig;
  logging?: boolean;
}

export interface AuthConfig {
  type: 'bearer' | 'api-key' | 'basic' | 'custom';
  tokenKey?: string;
  refreshEndpoint?: string;
  loginEndpoint?: string;
  logoutEndpoint?: string;
  onAuthFailure?: () => void;
}

export interface RetryConfig {
  maxRetries: number;
  retryDelay: number;
  retryOn: number[];
  exponentialBackoff: boolean;
}

// ═══════════════════════════════════════════
// REQUEST / RESPONSE
// ═══════════════════════════════════════════

export interface ApiRequestConfig {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  data?: unknown;
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
  timeout?: number;
  signal?: AbortSignal;
  skipAuth?: boolean;
  skipRetry?: boolean;
  cache?: boolean;
  transformResponse?: <T>(data: unknown) => T;
}

export interface ApiResponse<T = unknown> {
  data: T;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  meta?: ApiResponseMeta;
}

export interface ApiResponseMeta {
  timestamp: string;
  requestId?: string;
  pagination?: PaginationMeta;
  rateLimit?: RateLimitMeta;
}

export interface PaginationMeta {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface RateLimitMeta {
  limit: number;
  remaining: number;
  resetAt: string;
}

// ═══════════════════════════════════════════
// ERRORES TIPADOS (const object en vez de enum por erasableSyntaxOnly)
// ═══════════════════════════════════════════

export const ApiErrorCode = {
  // Network
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',
  ABORTED: 'ABORTED',

  // Auth
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  REFRESH_FAILED: 'REFRESH_FAILED',

  // Client
  BAD_REQUEST: 'BAD_REQUEST',
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  CONFLICT: 'CONFLICT',

  // Server
  SERVER_ERROR: 'SERVER_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',

  // Custom
  RATE_LIMITED: 'RATE_LIMITED',
  UNKNOWN: 'UNKNOWN',
} as const;

export type ApiErrorCode = (typeof ApiErrorCode)[keyof typeof ApiErrorCode];

export interface ApiError {
  code: ApiErrorCode;
  message: string;
  status?: number;
  details?: Record<string, unknown>;
  originalError?: Error;
  retryable: boolean;
  timestamp: string;
}

// ═══════════════════════════════════════════
// TOKENS
// ═══════════════════════════════════════════

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
  expiresAt?: number;
}

export interface AuthState {
  isAuthenticated: boolean;
  isRefreshing: boolean;
  tokens: TokenPair | null;
  user?: Record<string, unknown>;
}
